import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BosDurum, Buton, Iskelet, Kart, MetinAlani, NotKutusu, Secim } from "@koclukpanel/ui";
import { ApiHatasi, etkinlikDokumu, ogrenciKunyesi } from "../api";
import type { SekmeOzellikleri } from "./sekmeler";

/**
 * İŞLEM GEÇMİŞİ SEKMESİ — F-PPL-04 / G-IZL-03a
 *
 * ================================================================
 * BU SEKME AÇILIR AÇILMAZ VERİ ÇEKMİYOR — VE BU BİR TASARIM KARARI
 * ================================================================
 * Sunucu ölçüldü (05.09.2026, `etkinlik.service.ts` -> `kisi`):
 * başkasının etkinlik dökümünü açmak
 *
 *   1. `denetim.etkinlik.kisi` iznini **`ALL` kapsamıyla** ister,
 *   2. en az belirli uzunlukta **yazılı gerekçe** ister,
 *   3. ve açılışın kendisi **SENKRON** bir `SECURITY` olayı olarak
 *      `WARNING` şiddetiyle, gerekçesiyle birlikte kaydedilir.
 *
 * Yani bu sekmeye girmek "bir listeye bakmak" değil, **kayda geçen
 * bir işlem**. Sekme açılır açılmaz istek atsaydı, on dört sekme
 * arasında gezinen bir yönetici farkında olmadan güvenlik olayı
 * üretirdi — ve o olayların hiçbirinin anlamlı bir gerekçesi olmazdı.
 *
 * Bu, korumalı PDR notunun kapısıyla aynı desen (F-SES-05): koruma
 * unutulabilecek bir yerde değil, **işin kendisinde**.
 *
 * ================================================================
 * KİMLİK KÜNYEDEN GELİYOR — VE GELMİYORSA SEBEBİ YETKİDİR
 * ================================================================
 * Uç öğrencinin `Student.id`sini değil `User.id`sini istiyor. Künye
 * o alanı yalnızca `ALL` kapsamı olan kişiye koyuyor; anahtar hiç
 * dönmüyorsa açılacak bir kapı da yok.
 */

/** Uç bu aralıkları kabul ediyor; ekran kendi listesini uydurmuyor. */
const ARALIKLAR = [
  { deger: "7", etiket: "Son 7 gün" },
  { deger: "30", etiket: "Son 30 gün" },
  { deger: "90", etiket: "Son 90 gün" },
];

/** Sunucunun istediği en az gerekçe uzunluğu (`DOKUM_GEREKCE_EN_AZ`). */
const GEREKCE_EN_AZ = 10;

export function IslemGecmisi({ ogrenciId, ogrenciAdi }: SekmeOzellikleri) {
  const [gun, setGun] = useState("30");
  const [gerekce, setGerekce] = useState("");
  /** Gönderilmiş gerekçe — sorgunun anahtarı da bu, taslak DEĞİL. */
  const [acilan, setAcilan] = useState<{ gerekce: string; gun: string } | null>(null);

  const kunye = useQuery({
    queryKey: ["ogrenci-kunye", ogrenciId],
    queryFn: () => ogrenciKunyesi(ogrenciId),
    retry: false,
  });

  const kullaniciId = kunye.data?.kullaniciId ?? null;

  const dokum = useQuery({
    queryKey: ["etkinlik-dokumu", kullaniciId, acilan?.gun, acilan?.gerekce],
    queryFn: () => etkinlikDokumu(kullaniciId!, Number(acilan!.gun), acilan!.gerekce),
    retry: false,
    /*
      UC KOSUL DA GEREKLI: kimlik geldi, gerekce YAZILDI ve kullanici
      "ac" dedi. Biri eksikken sorgu kosarsa gereksiz bir guvenlik
      olayi yazilir.
    */
    enabled: kullaniciId !== null && acilan !== null,
    /*
      Acilis kayda gecen bir islem: arka planda kendiliginden
      TEKRARLANMAMALI. Varsayilan davranis pencereye her donuste
      yeniden isteyip her seferinde yeni bir SECURITY olayi uretirdi.
    */
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
  });

  if (kunye.isPending) return <Iskelet satir={3} />;

  if (kullaniciId === null) {
    /*
      Kunye kimligi dondurmediyse bu kullanicinin `ALL` kapsami yok.
      Sekme normalde zaten cizilmiyor (`sekmeler.tsx` kapsamı soruyor);
      buraya ancak izinler oturum ORTASINDA daralirsa gelinir
      (kullanici gorunumu — F-AUTH-07).
    */
    return (
      <BosDurum
        baslik="İşlem geçmişi açılamıyor"
        aciklama="Başkasının etkinlik dökümünü açmak için gereken yetki bu oturumda yok."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Kart
        baslik="İşlem geçmişi"
        aciklama={`${ogrenciAdi} adına sistemde yapılan işlemlerin dökümü` /* G-IZL-03a */}
      >
        <div className="flex flex-col gap-3">
          {/*
            ================================================================
            UYARI ÖNCE, FORM SONRA — SIRA BİLİNÇLİ
            ================================================================
            Kullanıcı ne olacağını gerekçeyi yazmadan ÖNCE okuyor.
            Uyarı düğmenin altında olsaydı, çoğu kişi onu ancak
            tıkladıktan sonra görürdü.
          */}
          <NotKutusu ton="uyari">
            Başkasının işlem geçmişini açmak <strong>güvenlik olayı olarak kaydedilir</strong>: kim
            açtı, ne zaman açtı ve yazdığınız gerekçe denetim kaydında görünür. Kayıt silinemez.
          </NotKutusu>

          <Secim etiket="Dönem" value={gun} onChange={(e) => setGun(e.target.value)}>
            {ARALIKLAR.map((a) => (
              <option key={a.deger} value={a.deger}>
                {a.etiket}
              </option>
            ))}
          </Secim>

          <MetinAlani
            etiket="Gerekçe"
            /*
              "Neden" ISTENIYOR, "ne" degil: gerekce metni denetim
              kaydina AYNEN giriyor ve altı ay sonra o satiri okuyan
              kisinin tek dayanagi bu cumle.
            */
            yardim={`En az ${GEREKCE_EN_AZ} karakter. Bu metin denetim kaydına aynen yazılır.`}
            value={gerekce}
            onChange={(e) => setGerekce(e.target.value)}
            rows={2}
          />

          <div>
            <Buton
              type="button"
              disabled={gerekce.trim().length < GEREKCE_EN_AZ}
              onClick={() => setAcilan({ gerekce: gerekce.trim(), gun })}
            >
              Dökümü aç
            </Buton>
          </div>
        </div>
      </Kart>

      {acilan !== null && (
        <Kart baslik="Döküm" aciklama={`Son ${acilan.gun} gün`}>
          {dokum.isPending ? (
            <Iskelet satir={4} />
          ) : dokum.isError ? (
            /*
              Sunucunun mesaji AYNEN gosteriliyor: "gerekce kisa" ile
              "yetkin yok" ayri sebepler ve ekran bunlari kendi
              tahminiyle birlestirirse kullanici yanlis seyi duzeltmeye
              calisir.
            */
            <p className="text-hata-700 text-sm">
              {dokum.error instanceof ApiHatasi ? dokum.error.message : "Döküm alınamadı."}
            </p>
          ) : dokum.data.toplamIslem === 0 ? (
            <p className="text-notr-600 text-sm">
              Bu dönemde kayıtlı işlem yok. (Kayıt yokluğu, işlem yapılmadığı anlamına gelir —
              ölçülemedi demek değil.)
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="text-notr-900 text-sm">
                Toplam <strong>{dokum.data.toplamIslem}</strong> işlem
              </p>

              <div>
                <h4 className="text-notr-700 mb-1 text-xs font-semibold tracking-wide uppercase">
                  Modüle göre
                </h4>
                <ul className="flex flex-col gap-1 text-sm">
                  {dokum.data.moduller.map((m) => (
                    <li key={m.anahtar} className="flex justify-between gap-4">
                      <span className="text-notr-700">{m.anahtar}</span>
                      <span className="text-notr-900 tabular-nums">{m.islemSayisi}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-notr-700 mb-1 text-xs font-semibold tracking-wide uppercase">
                  Güne göre
                </h4>
                <ul className="flex flex-col gap-1 text-sm">
                  {dokum.data.gunler.map((g) => (
                    <li key={g.gun} className="flex justify-between gap-4">
                      <span className="text-notr-700">
                        {new Date(`${g.gun}T00:00:00.000Z`).toLocaleDateString("tr-TR")}
                      </span>
                      <span className="text-notr-900 tabular-nums">{g.islemSayisi}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </Kart>
      )}
    </div>
  );
}
